import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  Calendar,
  DatePicker,
  Button,
  Typography,
  Spin,
  Space,
  ConfigProvider,
  Flex,
} from "antd";
import enGB from "antd/locale/en_GB";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";

import {
  ContactsOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import AttendanceModal from "./component/addModal";
import DetailModal from "./component/detailModal";
import CalendarDrawer from "./component/calendarDrawer";
import http from "@/utils/http";
import api from "@/utils/api";
import "./component/index.css";
const { Title } = Typography;
export default function BankIndex() {
  dayjs.locale("en-gb");
  const addModalRef = useRef(null);
  const detailModalRef = useRef(null);
  const [month, setMonth] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [calendarData, setCalendarData] = useState({
    dailyList: [],
    currentMonthWorkHours: 0,
    periodWorkHours: [],
    weeklyWorkHoursList: [],
  });
  const weekDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const handleOpenDetail = (item, date) => {
    detailModalRef.current?.open({
      ...item,
      date, 
    });
  };

  const handleDateChange = (value) => {
    if (!value) return;
    setMonth(value);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const handleSubmit = async (targetMonth = month) => {
    if (!targetMonth) return;

    setLoading(true);
    try {
      const response = await http.get(api.getMonthlyCalendarList, {
        params: { month: targetMonth.format("YYYY-MM") },
      });

      setCalendarData({
        dailyList: response?.data?.dailyList || [],
        currentMonthWorkHours: response?.data?.currentMonthWorkHours || 0,
        periodWorkHours: response?.data?.periodWorkHours || [],
        weeklyWorkHoursList: response?.data?.weeklyWorkHoursList || [],
      });
    } finally {
      setLoading(false);
    }
  };

  /** 时间数组转化 */
  const formatClock = (clockObj) => {
    if (!clockObj || !clockObj.punchTime || clockObj.punchTime.length < 4) {
      return null;
    }

    const punchTime = clockObj.punchTime;
    let h, min;

    if (punchTime.length >= 5) {
      h = punchTime[3];
      min = punchTime[4];
    } else if (punchTime.length === 4) {
      h = punchTime[3];
      min = 0; // 默认分钟为0
    } else {
      return null;
    }
    return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
  };
  const formatToAMPM = (timeStr) => {
    if (!timeStr || timeStr === "Check-in" || timeStr === "Check-out") {
      return timeStr;
    }

    try {
      const [hours, minutes] = timeStr.split(":").map(Number);
      const period = hours >= 12 ? "PM" : "AM";
      const formattedHours = hours % 12 || 12;
      return `${formattedHours}:${String(minutes).padStart(2, "0")} ${period}`;
    } catch (error) {
      return timeStr;
    }
  };

  const getListData = (value) => {
    const dateStr = value.format("YYYY-MM-DD");

    const dayData = calendarData.dailyList.find((item) => {
      if (!item.date) return false;

      // 处理不同格式的日期数组
      const [y, m, d] = item.date;

      const month = typeof m === "number" ? m : parseInt(m);
      const day = typeof d === "number" ? d : parseInt(d);

      const itemDateStr = `${y}-${String(month).padStart(2, "0")}-${String(
        day,
      ).padStart(2, "0")}`;

      return itemDateStr === dateStr;
    });

    if (!dayData) return [];

    const amIn = formatClock(dayData.amClockIn);
    const amOut = formatClock(dayData.amClockOut);
    const pmIn = formatClock(dayData.pmClockIn);
    const pmOut = formatClock(dayData.pmClockOut);

    return [
      {
        label: "Start",
        time: amIn || "In",
        attendanceId: dayData.amClockIn?.attendanceId,
        date: dayData.date,
        type: "1",
        empty: !amIn,
      },
      {
        label: "Lunch-Out",
        time: amOut || "Out",
        attendanceId: dayData.amClockOut?.attendanceId,
        date: dayData.date,
        type: "2",
        empty: !amOut,
      },
      {
        label: "Lunch-in",
        time: pmIn || "In",
        attendanceId: dayData.pmClockIn?.attendanceId,
        date: dayData.date,
        type: "3",
        empty: !pmIn,
      },
      {
        label: "End",
        time: pmOut || "Out",
        attendanceId: dayData.pmClockOut?.attendanceId,
        date: dayData.date,
        type: "4",
        empty: !pmOut,
      },
    ];
  };

  const cellRender = (current, info) => {
    if (info.type !== "date") return info.originNode;

    const listData = getListData(current);
    if (!listData.length) return null;

    // 获取当天总工时
    const dayData = calendarData.dailyList.find((item) => {
      if (!item.date) return false;
      const [y, m, d] = item.date;
      const itemDateStr = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      return itemDateStr === current.format("YYYY-MM-DD");
    });

    const totalWorkHours = dayData?.validWorkHours;
    const isGray = dayData?.currentMonth === false;

    const isFuture = current.isAfter(dayjs(), "day");
    const isDisabled = isGray || isFuture;

    return (
      <div
        className={`calendar-cell ${isDisabled ? "calendar-cell-gray" : ""}`}
        style={{ position: "relative" }}
      >
        <div
          style={{
            fontSize: 12,
            display: "flex",
            justifyContent: "flex-end",
            color: isDisabled ? "#999" : "#333",
          }}
        >
          {current.format("MM/DD/YYYY")}
        </div>
        {listData.map((item, index) => (
          <div
            key={index}
            className={`calendar-clock ${item.type.includes("AM") ? "am" : "pm"} ${
              item.empty ? "empty" : ""
            }`}
          >
            <span className="label">
              <ContactsOutlined style={{ marginRight: 4 }} />
              {item.label}
            </span>
            <br />

            {/* Check-in / Check-out */}
            {item.empty ? (
              <span
                className="time clickable"
                style={{
                  color: "#1677ff",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isDisabled) return;

                  addModalRef.current?.openAdd(item);
                }}
              >
                {item.time}
              </span>
            ) : (
              <span
                className="time"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isDisabled) return;

                  handleOpenDetail(item, current);
                }}
              >
                <CheckSquareOutlined
                  style={{ color: "#1677ff", marginRight: 4 }}
                />
                {formatToAMPM(item.time)}
              </span>
            )}
          </div>
        ))}

        {/* 单元格右下角显示当天总工时 */}
        {totalWorkHours > 0 && (
          <span className="valid-work-hours">
            <ClockCircleOutlined
              style={{ marginRight: 4, marginTop: 2, fontSize: 14 }}
            />
            {totalWorkHours} h
          </span>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!month) return;
    handleSubmit(month);
  }, [month]);

  return (
    <div className="dashboard-layout-payroll">
      <div className="calendar-header-payroll">
        <Title level={4}>Monthly Calendar</Title>
        <Space>
          <DatePicker
            picker="month"
            format="YYYY-MM"
            value={month}
            onChange={handleDateChange}
          />
          <Button type="primary" onClick={handleSubmit} loading={loading}>
            Submit
          </Button>
        </Space>
      </div>

      <Spin spinning={loading} className="calendar-spin">
        <Card className="calendar-layout">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              textAlign: "center",
              fontWeight: 500,
              marginBottom: 8,
            }}
          >
            {weekDays.map((day) => (
              <div key={day}>{day}</div>
            ))}
          </div>
          <ConfigProvider locale={enGB}>
            <Calendar
              value={month}
              cellRender={cellRender}
              className="custom-calendar"
              headerRender={() => null}
              fullscreen
              disabledDate={() => true}
            />
          </ConfigProvider>
        </Card>
      </Spin>

      <AttendanceModal ref={addModalRef} onSuccess={handleSubmit} />
      <DetailModal ref={detailModalRef} onSuccess={handleSubmit} />

      <CalendarDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        calendarData={calendarData}
        month={month}
      />
    </div>
  );
}
